import React from 'react';
import Autosuggest from 'react-autosuggest';
import { Scrollbars } from 'react-custom-scrollbars-2';

const SEARCH_DEBOUNCE_MS = 250;

// Local (non-search) filtering, kept for callers that already hold the full
// user list (e.g. AddUserToTeam).
const getSuggestions = (users, value) => {
  // Return empty array if no value or users
  if (!value || !value.trim().length || !users) {
    return [];
  }

  const inputValue = value.trim().toLowerCase();

  // Return empty if input is just whitespace
  if (inputValue.length === 0) {
    return [];
  }

  return users.filter((user) => {
    if (!user) return false;
    // Get normalized names, defaulting to empty strings
    const firstName = user?.firstName?.toLowerCase() || '';
    const lastName = user?.lastName?.toLowerCase() || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const reverseFullName = `${lastName} ${firstName}`.trim();
    const email = user?.email?.toLowerCase() || '';
    const phone = user?.phone || '';

    return (
      firstName.includes(inputValue) ||
      lastName.includes(inputValue) ||
      fullName.includes(inputValue) ||
      reverseFullName.includes(inputValue) ||
      email.includes(inputValue) ||
      phone.includes(inputValue)
    );
  });
};

// Use your imagination to render suggestions.
const renderSuggestion = (suggestion) => (
  <div>
    <div>
      {suggestion.firstName} {suggestion.lastName}
    </div>
    {(suggestion.email || suggestion?.company?.name) && (
      <div style={{ fontSize: 12, color: '#7c8698', marginTop: 2 }}>
        {[suggestion.email, suggestion?.company?.name]
          .filter(Boolean)
          .join(' · ')}
      </div>
    )}
  </div>
);

/**
 * UserPicker.
 *
 * Two modes:
 *  - `users`    — local array, filtered in the browser (legacy callers).
 *  - `onSearch` — async `(term) => Promise<user[]>`. Input is debounced
 *                 (250 ms), stale responses are dropped, and the dropdown
 *                 reports "Searching…" / "No matches" instead of sitting empty.
 *                 Use this for anything backed by a server query: the legacy
 *                 local mode required the FULL user payload to arrive before
 *                 the first keystroke could show a single match.
 *
 * `searchScope` (any value) forces a re-query when the scope changes, e.g. when
 * "Add external users too" is toggled.
 */
class UserPicker extends React.Component {
  constructor(props) {
    super(props);

    this.users = props.users;
    this.onSelected = props.onSelected;

    this.searchTimer = null;
    this.searchReqId = 0;

    // Autosuggest is a controlled component.
    // This means that you need to provide an input value
    // and an onChange handler that updates this value (see below).
    // Suggestions also need to be provided to the Autosuggest,
    // and they are initially empty because the Autosuggest is closed.
    this.state = {
      value: '',
      suggestions: [],
      loading: false,
      searched: false,
      error: false,
    };
  }

  get isSearchMode() {
    return typeof this.props.onSearch === 'function';
  }

  componentDidMount() {
    this.users = this.props.users;
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.isSearchMode) {
      if (prevProps.users !== this.props.users) {
        this.users = this.props.users;
        if (this.state.value && this.state.value.trim().length > 0) {
          this.setState({
            suggestions: getSuggestions(this.props.users, this.state.value),
          });
        }
      }
      return;
    }

    if (
      prevState.value !== this.state.value ||
      prevProps.searchScope !== this.props.searchScope
    ) {
      this.scheduleSearch(this.state.value);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.searchTimer);
    this.searchReqId += 1;
  }

  scheduleSearch = (value, allowEmpty = false) => {
    clearTimeout(this.searchTimer);
    const term = (value || '').trim();

    if (!term && !allowEmpty) {
      this.searchReqId += 1; // drop any in-flight response
      this.setState({
        suggestions: [],
        loading: false,
        searched: false,
        error: false,
      });
      return;
    }

    this.setState({ loading: true, error: false });
    this.searchTimer = setTimeout(() => this.runSearch(term), SEARCH_DEBOUNCE_MS);
  };

  runSearch = (term) => {
    const reqId = ++this.searchReqId;
    this.props
      .onSearch(term)
      .then((results) => {
        if (reqId !== this.searchReqId) return; // a newer query won
        this.setState({
          suggestions: Array.isArray(results) ? results : [],
          loading: false,
          searched: true,
          error: false,
        });
      })
      .catch(() => {
        if (reqId !== this.searchReqId) return;
        this.setState({
          suggestions: [],
          loading: false,
          searched: true,
          error: true,
        });
      });
  };

  onChange = (event, { newValue }) => {
    this.setState({
      value: newValue,
    });
  };

  // Autosuggest will call this function every time you need to update suggestions.
  onSuggestionsFetchRequested = ({ value, reason }) => {
    if (this.isSearchMode) {
      // Focusing an empty input returns the first page of matches rather than
      // an empty dropdown.
      this.scheduleSearch(value, reason === 'input-focused');
      return;
    }
    this.setState({
      suggestions: getSuggestions(this.users, value),
    });
  };

  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
      loading: false,
      searched: false,
      error: false,
    });
  };

  /** Reset the input + results (used after adding a user). */
  clear = () => {
    clearTimeout(this.searchTimer);
    this.searchReqId += 1;
    this.setState({
      value: '',
      suggestions: [],
      loading: false,
      searched: false,
      error: false,
    });
  };

  getSuggestionValue = (suggestion) => {
    let selectedUser = suggestion;
    if (this.onSelected != null) this.onSelected(selectedUser);
    return `${suggestion.firstName} ${suggestion.lastName}`;
  };

  renderSuggestionsContainer = ({ containerProps, children }) => {
    const { loading, searched, suggestions, error, value } = this.state;
    const term = (value || '').trim();
    const hasSuggestions = (suggestions || []).length > 0;

    const wrap = (content) => (
      <div {...containerProps}>
        <Scrollbars
          style={{ width: '100%' }}
          autoHeight
          autoHeightMax={300}
          autoHide>
          {content}
        </Scrollbars>
      </div>
    );

    if (this.isSearchMode) {
      if (hasSuggestions) {
        return wrap(children);
      }
      if (error) {
        return wrap(<div style={styles.status}>Search failed — please retry.</div>);
      }
      if (loading) {
        return wrap(<div style={styles.status}>{term ? 'Searching…' : 'Loading…'}</div>);
      }
      if (searched && term) {
        return wrap(<div style={styles.status}>{`No matches for “${term}”`}</div>);
      }
      // Nothing to show: no empty dropdown box.
      return null;
    }

    return hasSuggestions ? wrap(children) : null;
  };

  render() {
    const { value, suggestions } = this.state;

    // Autosuggest will pass through all these props to the input.
    const inputProps = {
      placeholder: 'Add User',
      value: value,
      onChange: this.onChange,
    };

    // Finally, render it!
    return (
      <Autosuggest
        theme={styles}
        alwaysRenderSuggestions
        highlightFirstSuggestion
        suggestions={suggestions ? suggestions : []}
        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        getSuggestionValue={this.getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={this.renderSuggestionsContainer}
        inputProps={inputProps}
      />
    );
  }
}

export default UserPicker;

const styles = {
  container: {
    position: 'relative',
  },
  input: {
    width: '280px',
    height: '52px',
    padding: '10px 20px',
    fontFamily: 'Helvetica, sans-serif',
    fontWeight: '300',
    fontSize: '16px',
    border: '1px solid #aaa',
    borderRadius: '4px',
  },
  inputFocused: {
    outline: 'none',
  },
  inputOpen: {
    borderBottomLeftRadius: '0',
    borderBottomRightRadius: '0',
  },
  suggestionsContainer: {
    display: 'none',
  },
  suggestionsContainerOpen: {
    display: 'block',
    position: 'absolute',
    top: '51px',
    width: '280px',
    border: '1px solid #aaa',
    backgroundColor: '#fff',
    fontFamily: 'Helvetica, sans-serif',
    fontWeight: '300',
    fontSize: '16px',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    zIndex: '2',
  },
  suggestionsList: {
    margin: '0',
    padding: '0',
    listStyleType: 'none',
  },
  suggestion: {
    cursor: 'pointer',
    padding: '10px 20px',
  },
  suggestionHighlighted: {
    backgroundColor: '#ddd',
  },
  status: {
    padding: '10px 20px',
    color: '#7c8698',
    fontSize: '14px',
  },
};
